import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { conversationService } from "@/features/conversations/services/conversationService";
import { fetchAgentsOnce, setAgentSlug, setSelectedAgent } from "@/store/agentsSlice";

type Conversation = {
  conversationId: string;
  userId: string;
  agent: {
    agentId: string;
    agentName: string;
  };
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
};

type ConversationsState = {
  conversationId: string;
  conversations: Conversation[];
  conversationsLoaded: boolean;
};

const initialState: ConversationsState = {
  conversationId: "",
  conversations: [],
  conversationsLoaded: false,
};

const conversationsSlice = createSlice({
  name: "conversations",
  initialState,
  reducers: {
    setConversations(state, action: PayloadAction<Conversation[]>) {
      state.conversations = action.payload;
      state.conversationsLoaded = true;
    },
    setConversationId(state, action: PayloadAction<string | null>) {
      state.conversationId = action.payload || "";
    },
    clearConversationId(state) {
      state.conversationId = "";
      state.conversationsLoaded = false;
    },
    updateConversationTitle(
      state,
      action: PayloadAction<{ conversationId: string; title: string }>
    ) {
      const { conversationId, title } = action.payload;
      const conv = state.conversations.find(
        (c) => c.conversationId === conversationId
      );
      if (conv) conv.title = title;
    },
    removeConversation(state, action: PayloadAction<string>) {
      state.conversations = state.conversations.filter(
        (c) => c.conversationId !== action.payload
      );
    },
  },
});

export const {
  setConversations,
  setConversationId,
  clearConversationId,
  updateConversationTitle,
  removeConversation,
} = conversationsSlice.actions;
export default conversationsSlice.reducer;

export const fetchConversationsForAgent = (agentId: string) => async (dispatch) => {
  try {
    const conversations: any = await conversationService.fetchConversationsByAgent(agentId);
    dispatch(setConversations(conversations));
  } catch (error) {
    console.error("Failed to fetch conversations:", error);
    dispatch(setConversations([]));
  }
};

export const fetchAllConversations = () => async (dispatch) => {
  try {
    const conversations: any = await conversationService.listConversations();
    dispatch(setConversations(conversations));
  } catch (error) {
    console.error("Failed to fetch all conversations:", error);
    dispatch(setConversations([]));
  }
};

export const createConversationAndFetch =
  (agentId: string, initialMessage?: string) => async (dispatch: any) => {
    try {
      const newConversation = await conversationService.createConversation({
        agent_id: agentId,
        agentId: agentId,
        title: initialMessage,
      });

      dispatch(setConversationId(newConversation.conversationId));
      await dispatch(fetchAllConversations());

      return newConversation;
    } catch (error) {
      console.error("Failed to create conversation:", error);
      throw error;
    }
  };

export const loadConversationWithMessages =
  (conversationId: string) => async (dispatch: any, getState: any) => {
    try {
      const conversation: any = await conversationService.getConversation(conversationId);
      const conversationAgentId =
        conversation.agent?.agentId || (conversation as any).agentId;

      if (conversationAgentId) {
        const { agentsLoaded } = getState().agents;
        if (!agentsLoaded) {
          await dispatch(fetchAgentsOnce());
        }

        const stateAgents = getState().agents.agents;
        const agent =
          stateAgents.find((a: any) => a.agentId === conversationAgentId) ||
          (conversation.agent
            ? {
                agentId: conversation.agent.agentId,
                agentName: conversation.agent.agentName,
              }
            : null);

        if (agent) {
          dispatch(setAgentSlug(conversationAgentId));
          dispatch(setSelectedAgent(agent));
        }
      }

      return conversation;
    } catch (error) {
      console.error("Failed to load conversation:", error);
      throw error;
    }
  };

export const renameConversation =
  (conversationId: string, newTitle: string) => async (dispatch: any) => {
    try {
      dispatch(updateConversationTitle({ conversationId, title: newTitle }));
      await conversationService.updateConversation(conversationId, {
        title: newTitle,
      });
    } catch (error) {
      console.error("Rename failed:", error);
    }
  };

export const deleteConversationThunk =
  (conversationId: string) => async (dispatch: any) => {
    try {
      dispatch(removeConversation(conversationId));
      await conversationService.deleteConversation(conversationId);
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };
